import React from "react";
import { Plus, Trash2, Send, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inputCls, formatCOP } from "@/components/common";

export const ClientSelect = ({ clients, value, onChange, testId = "doc-client-select" }) => (
  <select data-testid={testId} required className={inputCls} value={value} onChange={e => onChange(e.target.value)}>
    <option value="">Selecciona un cliente</option>
    {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
  </select>
);

export const DocTable = ({ headers, children }) => (
  <div className="bg-[#15171E] border border-[#262A33] rounded-2xl overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#262A33] text-[#9CA3AF]">
            {headers.map((h, i) => (
              <th key={i} className={`font-medium px-5 py-3 ${h.right ? "text-right" : "text-left"}`}>{h.label ?? h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

export const IconBtn = ({ children, className = "", ...props }) => (
  <button {...props} className={`p-1.5 rounded-lg transition-colors ${className}`}>{children}</button>
);

export const itemsTotal = (items) => items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.unit_price || 0), 0);

export const LineItemsEditor = ({ items, onChange, services = [], withName = true, testPrefix = "item" }) => {
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, withName ? { service_id: "", name: "", description: "", qty: 1, unit_price: 0 } : { qty: 1, description: "", unit_price: 0 }]);
  const addFromCatalog = (sid) => {
    const s = services.find(x => x.service_id === sid);
    if (!s) return;
    onChange([...items, { service_id: s.service_id, name: s.name, description: s.description || "", qty: 1, unit_price: s.unit_price }]);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-medium text-[#9CA3AF]">Ítems</span>
        <div className="flex gap-2">
          {withName && services.length > 0 && (
            <select data-testid={`${testPrefix}-catalog-select`} className={`${inputCls} !w-auto !py-1.5 text-xs`} value="" onChange={e => addFromCatalog(e.target.value)}>
              <option value="">+ Desde catálogo</option>
              {services.map(s => <option key={s.service_id} value={s.service_id}>{s.name} · {formatCOP(s.unit_price)}</option>)}
            </select>
          )}
          <button type="button" data-testid={`${testPrefix}-add-line`} onClick={add} className="inline-flex items-center gap-1 text-xs text-[#F97316] hover:text-[#EA580C] font-medium">
            <Plus className="w-3.5 h-3.5"/> Agregar línea
          </button>
        </div>
      </div>
      {items.length === 0 && <div className="text-xs text-[#6B7280] border border-dashed border-[#262A33] rounded-xl p-4 text-center">Sin ítems. Agrega al menos uno.</div>}
      {items.map((it, idx) => (
        <div key={idx} data-testid={`${testPrefix}-row-${idx}`} className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-3 space-y-2">
          <div className="flex gap-2">
            {withName && (
              <input data-testid={`${testPrefix}-name-${idx}`} placeholder="Servicio *" required className={inputCls} value={it.name} onChange={e => update(idx, { name: e.target.value })}/>
            )}
            <input data-testid={`${testPrefix}-desc-${idx}`} placeholder={withName ? "Descripción (opcional)" : "Descripción *"} required={!withName} className={inputCls} value={it.description} onChange={e => update(idx, { description: e.target.value })}/>
            <button type="button" onClick={() => remove(idx)} className="p-2 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10" aria-label="Quitar">
              <Trash2 className="w-4 h-4"/>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 items-center">
            <input data-testid={`${testPrefix}-qty-${idx}`} type="number" min="0" step="any" placeholder="Cant." className={inputCls} value={it.qty} onChange={e => update(idx, { qty: e.target.value })}/>
            <input data-testid={`${testPrefix}-price-${idx}`} type="number" min="0" placeholder="Vlr. unitario" className={inputCls} value={it.unit_price} onChange={e => update(idx, { unit_price: e.target.value })}/>
            <div className="text-right text-sm font-medium text-[#F5F5F7] pr-1">{formatCOP(Number(it.qty || 0) * Number(it.unit_price || 0))}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const TotalsBox = ({ subtotal, discount = 0 }) => (
  <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-3 text-sm space-y-1">
    {discount > 0 && (
      <>
        <div className="flex justify-between text-[#9CA3AF]"><span>Subtotal</span><span>{formatCOP(subtotal)}</span></div>
        <div className="flex justify-between text-[#9CA3AF]"><span>Descuento</span><span>- {formatCOP(discount)}</span></div>
      </>
    )}
    <div className="flex justify-between font-bold text-[#F5F5F7]"><span>Total</span><span data-testid="doc-total" className="text-[#F97316]">{formatCOP(Math.max(subtotal - discount, 0))}</span></div>
  </div>
);

export const normalizeItems = (items) => items.map(i => ({ ...i, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 }));

export const sendDocEmail = async (path, clientEmail) => {
  if (clientEmail === "") { toast.error("El cliente no tiene email configurado"); return false; }
  try {
    const { data } = await api.post(`${path}/send-email`);
    toast.success(`Enviado a ${data.to}`); return true;
  } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar el email"); return false; }
};

export const copyPublicLink = async (path) => {
  try {
    const { data } = await api.get(`${path}/public-link`);
    const url = `${window.location.origin}/doc/${data.token}`;
    await navigator.clipboard?.writeText(url);
    toast.success("Enlace público copiado");
  } catch { toast.error("No se pudo copiar el enlace"); }
};

export const ShareButtons = ({ path, clientEmail, testId, onSent }) => (
  <>
    <IconBtn data-testid={`${testId}-send`} onClick={async () => { if (await sendDocEmail(path, clientEmail)) onSent?.(); }} className="text-[#9CA3AF] hover:text-sky-400 hover:bg-sky-500/10" title="Enviar por email al cliente" aria-label="Enviar email"><Send className="w-4 h-4"/></IconBtn>
    <IconBtn data-testid={`${testId}-link`} onClick={() => copyPublicLink(path)} className="text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#262A33]" title="Copiar enlace público" aria-label="Copiar enlace"><Link2 className="w-4 h-4"/></IconBtn>
  </>
);
